import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './ManageStore.css';

const ManageStore = ({ user, merchantInfo, merchantInfoError, onBack, onLoadMerchantInfo, onFetchGoodsList }) => {
  const { t } = useTranslation();
  const [goodsList, setGoodsList] = useState([]);
  const [goodsListError, setGoodsListError] = useState(false);
  const [loadingGoods, setLoadingGoods] = useState(false);
  
  // 分页相关状态
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // 获取商品列表的函数
  const fetchGoodsList = useCallback((merchantId, page = currentPage, size = pageSize) => {
    if (!merchantId || !onFetchGoodsList) return;
    
    setLoadingGoods(true);
    setGoodsListError(false);
    
    onFetchGoodsList(merchantId, page, size)
      .then(data => {
        setGoodsList(data.goodsList || []);
        setTotalPages(data.pagination?.totalPages || 1);
        setTotalItems(data.pagination?.totalItems || 0);
        setLoadingGoods(false);
      })
      .catch(error => {
        console.error('获取商品列表失败:', error);
        setGoodsListError(true);
        setLoadingGoods(false);
      });
  }, [onFetchGoodsList, currentPage, pageSize]);

  // Event handlers for pagination
  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    if (merchantInfo && merchantInfo.merchant_id) {
      fetchGoodsList(merchantInfo.merchant_id, 1, newSize);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      if (merchantInfo && merchantInfo.merchant_id) {
        fetchGoodsList(merchantInfo.merchant_id, newPage, pageSize);
      }
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      if (merchantInfo && merchantInfo.merchant_id) {
        fetchGoodsList(merchantInfo.merchant_id, newPage, pageSize);
      }
    }
  };

  // 组件挂载时加载店铺信息
  useEffect(() => {
    if (user && !merchantInfo && !merchantInfoError && onLoadMerchantInfo) {
      onLoadMerchantInfo();
    }
  }, [user, merchantInfo, merchantInfoError, onLoadMerchantInfo]);

  // 当店铺信息加载成功后，获取商品列表
  useEffect(() => {
    if (merchantInfo && merchantInfo.merchant_id) {
      fetchGoodsList(merchantInfo.merchant_id);
    }
  }, [merchantInfo, fetchGoodsList]);

  return (
    <div className="manage-store-container">
      <div className="manage-store-header">
        <button className="manage-store-back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
        <h2 className="manage-store-title">{t('profile.manageStore', '管理店铺')}</h2>
      </div>
      
      <div className="manage-store-content">
        {merchantInfo ? (
          <>
            <div className="store-info-card">
              <div className="store-header">
                <div className="store-image">
                  <img 
                    src={merchantInfo.store_image || "/images/store-placeholder.png"} 
                    alt="Store" 
                  />
                </div>
                <div className="store-details">
                  <h3 className="store-name">{merchantInfo.store_name || "店铺名称"}</h3>
                  <div className="store-status">
                    <span className={`status-indicator ${merchantInfo.is_open ? 'status-open' : 'status-closed'}`}></span>
                    <span>{merchantInfo.is_open ? t('profile.storeOpen', '营业中') : t('profile.storeClosed', '已打烊')}</span>
                  </div>
                </div>
              </div>
              
              <div className="info-section">
                <h4 className="info-section-title">{t('profile.storeInfo', '店铺信息')}</h4>
                <div className="info-item">
                  <div className="info-label">{t('profile.storeAddress', '地址')}</div>
                  <div className="info-value">{merchantInfo.address || "暂无地址信息"}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">{t('profile.storePhone', '电话')}</div>
                  <div className="info-value">{merchantInfo.phone || "暂无电话信息"}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">{t('profile.storeDescription', '描述')}</div>
                  <div className="info-value">{merchantInfo.description || "暂无描述信息"}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">{t('profile.businessType', '业务类型')}</div>
                  <div className="info-value">{merchantInfo.business_type || "暂无业务类型信息"}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">{t('profile.businessHours', '营业时间')}</div>
                  <div className="info-value">{merchantInfo.business_time_desc || "暂无营业时间信息"}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">{t('profile.minOrderAmount', '最低订单金额')}</div>
                  <div className="info-value">{merchantInfo.min_order_amount || "0"}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">{t('profile.shopType', '店铺类型')}</div>
                  <div className="info-value">{merchantInfo.shop_type || "暂无店铺类型信息"}</div>
                </div>
              </div>
              
              <div className="info-section">
                <h4 className="info-section-title">{t('profile.businessStats', '经营统计')}</h4>
                <div className="stats-grid">
                  <div className="stat-item">
                    <div className="stat-label">{t('profile.currentMonthOrders', '本月订单数')}</div>
                    <div className="stat-value">{merchantInfo.current_month_orders || "0"}</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-label">{t('profile.currentMonthCancelledOrders', '本月取消订单数')}</div>
                    <div className="stat-value">{merchantInfo.current_month_cancelled_orders || "0"}</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-label">{t('profile.currentMonthRevenue', '本月营收')}</div>
                    <div className="stat-value">{merchantInfo.current_month_revenue || "0"}</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-label">{t('profile.balance', '账户余额')}</div>
                    <div className="stat-value">{merchantInfo.balance || "0"}</div>
                  </div>
                </div>
              </div>
              
              <div className="info-section">
                <h4 className="info-section-title">{t('profile.goodsList', '商品列表')}</h4>
                
                {/* Pagination controls */}
                <div className="pagination-controls">
                  <div className="pagination-info">
                    <span>第 {currentPage} 页，共 {totalPages} 页 | 总计 {totalItems} 件商品</span>
                  </div>
                  <div className="pagination-actions">
                    <div className="page-size-selector">
                      <label htmlFor="page-size">每页显示：</label>
                      <select 
                        id="page-size"
                        value={pageSize} 
                        onChange={handlePageSizeChange}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                    </div>
                    <div className="page-navigation">
                      <button 
                        onClick={goToPreviousPage} 
                        disabled={currentPage <= 1 || loadingGoods}
                        className="page-button prev-button"
                      >
                        &lt;
                      </button>
                      <span className="current-page">{currentPage}/{totalPages}</span>
                      <button 
                        onClick={goToNextPage} 
                        disabled={currentPage >= totalPages || loadingGoods}
                        className="page-button next-button"
                      >
                        &gt;
                      </button>
                    </div>
                  </div>
                </div>
                
                {loadingGoods ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <div className="loading-text">{t('profile.loadingGoods', '正在加载商品列表...')}</div>
                  </div>
                ) : goodsListError ? (
                  <div className="error-container">
                    <div className="error-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                    </div>
                    <div className="error-text">{t('profile.loadGoodsError', '加载商品列表失败')}</div>
                    <button className="refresh-btn" onClick={() => fetchGoodsList(merchantInfo.merchant_id)}>
                      {t('profile.refresh', '刷新')}
                    </button>
                  </div>
                ) : goodsList.length > 0 ? (
                  <div className="goods-list">
                    {goodsList.map((item, index) => (
                      <div key={index} className="goods-item">
                        <div className="goods-image">
                          <img src={item.goods_image || "/images/product-placeholder.png"} alt={item.goods_name || "商品"} />
                        </div>
                        <div className="goods-details">
                          <h5 className="goods-name">{item.goods_name || "商品名称"}</h5>
                          <div className="goods-price">${item.goods_price || "0"}</div>
                          <div className="goods-description">{item.goods_brief || "暂无描述"}</div>
                          {item.category_name && (
                            <div className="goods-category">分类: {item.category_name}</div>
                          )}
                          <div className="goods-status">
                            {item.is_on_sale ? (
                              <span className="status-badge on-sale">在售</span>
                            ) : (
                              <span className="status-badge off-sale">下架</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-goods-container">
                    <div className="empty-goods-text">{t('profile.noGoods', '暂无商品')}</div>
                  </div>
                )}
              </div>
              
              <div className="action-buttons">
                <button className="action-btn success-btn">
                  {t('profile.editStoreInfo', '编辑店铺信息')}
                </button>
                <button className="action-btn primary-btn">
                  {t('profile.viewOrders', '查看订单')}
                </button>
              </div>
            </div>
          </>
        ) : merchantInfoError ? (
          <div className="error-container">
            <div className="error-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <div className="error-text">{t('profile.loadStoreInfoError', '加载店铺信息失败')}</div>
            <button className="refresh-btn" onClick={onLoadMerchantInfo}>
              {t('profile.refresh', '刷新')}
            </button>
          </div>
        ) : (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <div className="loading-text">{t('profile.loadingStoreInfo', '正在加载店铺信息...')}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageStore;